import moment from 'moment';

export const PERIOD_OPTIONS = [
  { value: '3m', label: '3 meses' },
  { value: '6m', label: '6 meses' },
  { value: '1y', label: '1 ano' },
  { value: 'all', label: 'Tudo' },
];

export const CHART_GROUP_BY_CODE = {
  PAS: 'blood_pressure',
  PAD: 'blood_pressure',
  FC: 'heart_rate',
  PESO: 'weight',
  ALTURA: 'height',
  PC: 'head_circumference',
  IMC: 'bmi',
};

export const CHART_GROUP_LABELS = {
  blood_pressure: 'Pressão arterial',
  heart_rate: 'Frequência cardíaca',
  weight: 'Peso',
  height: 'Comprimento ou altura',
  head_circumference: 'Perímetro cefálico',
  bmi: 'Índice de Massa Corporal',
};

export const CHART_GROUP_COLORS = {
  blood_pressure: ['#F1416C', '#009EF7'],
  heart_rate: ['#50CD89'],
  weight: ['#7239EA'],
  height: ['#FFC700'],
  head_circumference: ['#FF6B6B'],
  bmi: ['#00ADA3'],
};

export const REFERENCE_RANGES = {
  PAS: { min: 90, max: 120, label: 'Faixa usual' },
  PAD: { min: 60, max: 80, label: 'Faixa usual' },
  FC: { min: 60, max: 100, label: 'Faixa usual' },
  IMC: { min: 18.5, max: 24.9, label: 'Peso normal' },
};

const SERIES_COLORS = ['#009EF7', '#F1416C', '#50CD89', '#FFC700', '#7239EA', '#00ADA3'];

export function filterRecordsByPeriod(records, period) {
  if (!records?.length || period === 'all') {
    return [...(records ?? [])];
  }

  const months = period === '3m' ? 3 : period === '6m' ? 6 : 12;
  const cutoff = moment().subtract(months, 'months').startOf('day');

  return records.filter((record) => moment(record.created_at).isSameOrAfter(cutoff));
}

export function sortRecordsChronologically(records) {
  return [...records].sort(
    (a, b) => moment(a.created_at).valueOf() - moment(b.created_at).valueOf()
  );
}

export function sortRecordsNewestFirst(records) {
  return [...records].sort(
    (a, b) => moment(b.created_at).valueOf() - moment(a.created_at).valueOf()
  );
}

export function getIndicatorValue(record, indicatorId) {
  const indicator = record.health_indicators?.find(
    (item) => item.id_health_indicator === indicatorId
  );

  if (!indicator) {
    return null;
  }

  const raw = indicator.patient_health_data_values?.health_data_value;
  const parsed = parseFloat(raw);

  return Number.isFinite(parsed) ? parsed : null;
}

export function buildSummaryCards(records, healthIndicators) {
  const sorted = sortRecordsNewestFirst(records);

  return (healthIndicators ?? []).map((indicator) => {
    const latestValue = sorted.length ? getIndicatorValue(sorted[0], indicator.id_health_indicator) : null;
    const previousValue = sorted.length > 1 ? getIndicatorValue(sorted[1], indicator.id_health_indicator) : null;

    let delta = null;
    let trend = 'stable';

    if (latestValue !== null && previousValue !== null) {
      delta = latestValue - previousValue;
      const threshold = Math.abs(previousValue) * 0.02 || 0.5;
      if (delta > threshold) {
        trend = 'up';
      } else if (delta < -threshold) {
        trend = 'down';
      }
    }

    const reference = REFERENCE_RANGES[indicator.health_indicator_code] ?? null;
    let status = 'neutral';

    if (latestValue !== null && reference) {
      if (latestValue >= reference.min && latestValue <= reference.max) {
        status = 'normal';
      } else if (latestValue < reference.min) {
        status = 'low';
      } else {
        status = 'high';
      }
    }

    return {
      id: indicator.id_health_indicator,
      code: indicator.health_indicator_code,
      name: indicator.health_indicator_name,
      unit: indicator.scale?.scale_initials ?? '',
      latestValue,
      previousValue,
      delta,
      trend,
      status,
      reference,
      latestDate: sorted[0]?.created_at ?? null,
    };
  });
}

export function buildChartGroups(healthIndicators) {
  const groups = new Map();

  (healthIndicators ?? []).forEach((indicator) => {
    const groupKey = CHART_GROUP_BY_CODE[indicator.health_indicator_code]
      ?? `indicator_${indicator.id_health_indicator}`;

    if (!groups.has(groupKey)) {
      groups.set(groupKey, {
        key: groupKey,
        label: CHART_GROUP_LABELS[groupKey] ?? indicator.health_indicator_name,
        indicators: [],
      });
    }

    groups.get(groupKey).indicators.push(indicator);
  });

  return Array.from(groups.values());
}

export function buildChartSeries(records, indicators) {
  const sorted = sortRecordsChronologically(records);
  const categories = sorted.map((record) =>
    moment(record.created_at).format('DD/MM/YY')
  );

  const series = indicators.map((indicator, index) => ({
    name: indicator.health_indicator_name,
    code: indicator.health_indicator_code,
    unit: indicator.scale?.scale_initials ?? '',
    color: SERIES_COLORS[index % SERIES_COLORS.length],
    data: sorted.map((record) => getIndicatorValue(record, indicator.id_health_indicator)),
  }));

  return { categories, series, sorted };
}

export function buildReferenceAnnotations(indicators) {
  const annotations = [];

  indicators.forEach((indicator) => {
    const reference = REFERENCE_RANGES[indicator.health_indicator_code];
    if (!reference) {
      return;
    }

    annotations.push({
      y: reference.min,
      y2: reference.max,
      borderColor: '#50CD89',
      fillColor: '#50CD89',
      opacity: 0.12,
      label: {
        text: reference.label,
        style: { background: 'transparent', color: '#50CD89', fontSize: '10px' },
      },
    });
  });

  return annotations;
}

export function formatDelta(delta, unit) {
  if (delta === null || delta === undefined) {
    return null;
  }

  const sign = delta > 0 ? '+' : '';
  const formatted = Number.isInteger(delta) ? delta : delta.toFixed(1);

  return `${sign}${formatted}${unit ? ` ${unit}` : ''}`;
}

export function formatValue(value, unit, decimals = 1) {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  const parsed = typeof value === 'number' ? value : parseFloat(value);

  if (!Number.isFinite(parsed)) {
    return '—';
  }

  const formatted = parsed.toFixed(decimals);

  return unit ? `${formatted} ${unit}` : formatted;
}

export function getIndicatorDecimals(indicator) {
  const code = indicator?.health_indicator_code ?? indicator?.code ?? '';

  if (code === 'IMC') {
    return 1;
  }

  return 1;
}

export function formatHealthIndicatorValue(value, indicator) {
  const unit = indicator?.scale?.scale_initials ?? indicator?.unit ?? '';

  return formatValue(value, unit, getIndicatorDecimals(indicator));
}
